import { useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import {
  ADMISSION_INTAKES,
  ADMISSION_ROUND_STATUSES,
  type AdmissionApplication,
  type AdmissionIntake,
  type AdmissionRound,
  type AdmissionsCriteria,
  type Page,
  type School,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

/**
 * Admissions round management + the batch placement engine, and the
 * application queue behind it (packages/modules/m28-education/src/routes/admissions.ts
 * + src/service/placement.ts). Every write here requires ADMISSIONS_OFFICER;
 * accept/decline of an offer is guardian-only (see the route's own
 * ctx.nin === app.guardianNin check) so this console can view and withdraw
 * on a guardian's behalf but never accept/decline for them.
 */
export function Admissions() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('ADMISSIONS_OFFICER') ?? false;
  const [tab, setTab] = useState<'rounds' | 'applications' | 'criteria'>('rounds');

  return (
    <div>
      <PageHeader title="Admissions" subtitle="Intake rounds, the published placement formula, and the application queue." />
      <Tabs
        tabs={[
          { key: 'rounds', label: 'Rounds & Placement' },
          { key: 'applications', label: 'Applications' },
          { key: 'criteria', label: 'Published Criteria' },
        ]}
        value={tab}
        onChange={setTab}
      />
      {!isOfficer ? (
        <div className="mb-4">
          <ErrorNote message="Rounds and the officer application queue require the Admissions Officer role." />
        </div>
      ) : null}
      {tab === 'rounds' ? <RoundsTab isOfficer={isOfficer} /> : tab === 'applications' ? <ApplicationsTab isOfficer={isOfficer} /> : <CriteriaTab />}
    </div>
  );
}

function CriteriaTab() {
  const criteriaQ = useApiQuery<AdmissionsCriteria>('/api/v1/m28/admissions/criteria');
  if (criteriaQ.loading) return <Spinner />;
  if (criteriaQ.error) return <ErrorNote message={criteriaQ.error} />;
  const c = criteriaQ.data;
  if (!c) return null;
  return (
    <div className="card p-5">
      <h2 className="mb-1 text-sm font-bold text-slate-700">Placement scoring formula (max {c.maxScore} points)</h2>
      <p className="mb-4 text-xs text-slate-500">{c.algorithm}</p>
      <div className="space-y-2">
        {c.criteria.map((cr) => (
          <div key={cr.code} className="flex items-start justify-between rounded-lg border border-slate-200 px-3 py-2">
            <div>
              <div className="text-sm font-semibold text-slate-800">{cr.label}</div>
              <div className="text-xs text-slate-500">{cr.note}</div>
            </div>
            {cr.points !== undefined ? <div className="shrink-0 text-sm font-bold tabular-nums text-brand-600">+{cr.points}</div> : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function RoundsTab({ isOfficer }: { isOfficer: boolean }) {
  const [intake, setIntake] = useState('');
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (intake) qs.set('intake', intake);
  if (status) qs.set('status', status);
  const listQ = useApiQuery<Page<AdmissionRound>>(isOfficer ? `/api/v1/m28/admissions/rounds?${qs.toString()}` : null);
  const schoolsQ = useApiQuery<Page<School>>('/api/v1/m28/schools?limit=100');

  if (!isOfficer) return null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          <select className="field-input !w-40" value={intake} onChange={(e) => setIntake(e.target.value)}>
            <option value="">All intakes</option>
            {ADMISSION_INTAKES.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
          <select className="field-input !w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {ADMISSION_ROUND_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + New round
        </button>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No rounds yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Intake</th>
                <th className="table-head">Status</th>
                <th className="table-head text-right">Schools</th>
                <th className="table-head">Opened</th>
                <th className="table-head">Ran</th>
                <th className="table-head">Closed</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(r.id)}>
                  <td className="table-cell font-medium">{r.intake}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="table-cell text-right tabular-nums">{r.schoolIds.length}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(r.openedAt)}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(r.ranAt)}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(r.closedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateRoundModal
          schools={schoolsQ.data?.items ?? []}
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
      {detailId ? (
        <RoundDetailModal
          id={detailId}
          schools={schoolsQ.data?.items ?? []}
          onClose={() => setDetailId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function CreateRoundModal({ schools, onClose, onDone }: { schools: School[]; onClose: () => void; onDone: () => void }) {
  const [intake, setIntake] = useState<AdmissionIntake>('P1');
  const [schoolIds, setSchoolIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const toggle = (id: string) => setSchoolIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m28/admissions/rounds', { intake, schoolIds });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create round');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New admission round" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Intake">
          <select className="field-input !w-40" value={intake} onChange={(e) => setIntake(e.target.value as AdmissionIntake)}>
            {ADMISSION_INTAKES.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Participating schools">
          <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
            {schools.map((s) => (
              <label key={s.id} className="flex items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-slate-50">
                <input type="checkbox" checked={schoolIds.includes(s.id)} onChange={() => toggle(s.id)} />
                {s.name} <span className="text-xs text-slate-400">({s.district})</span>
              </label>
            ))}
          </div>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || schoolIds.length === 0}>
            {busy ? 'Creating…' : 'Create round'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RoundDetailModal({
  id,
  schools,
  onClose,
  onChanged,
}: {
  id: string;
  schools: School[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const detailQ = useApiQuery<AdmissionRound>(`/api/v1/m28/admissions/rounds/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [lastRun, setLastRun] = useState<{ offers: number; waitlisted: number } | null>(null);

  const round = detailQ.data;
  const schoolName = (sid: string) => schools.find((s) => s.id === sid)?.name ?? sid;

  const run = async (path: string) => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.post<AdmissionRound>(`/api/v1/m28/admissions/rounds/${id}${path}`);
      if (path === '/run' && updated.offers !== undefined) setLastRun({ offers: updated.offers, waitlisted: updated.waitlisted ?? 0 });
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Admission round" onClose={onClose} wide>
      {detailQ.loading || !round ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={round.status} />
            <span className="font-semibold">{round.intake} intake</span>
          </div>
          {lastRun ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
              Placement run complete — {lastRun.offers} offers issued, {lastRun.waitlisted} waitlisted.
            </div>
          ) : null}
          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Capacity snapshot</div>
            {Object.keys(round.capacitySnapshot).length === 0 ? (
              <p className="text-xs text-slate-400">No snapshot yet — open the round to snapshot each school's remaining capacity.</p>
            ) : (
              <ul className="space-y-1 text-xs">
                {Object.entries(round.capacitySnapshot).map(([sid, count]) => (
                  <li key={sid} className="flex justify-between rounded-lg border border-slate-200 px-2.5 py-1.5">
                    <span>{schoolName(sid)}</span>
                    <span className="font-semibold tabular-nums">{count} remaining</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {round.status === 'DRAFT' ? (
              <button className="btn-primary !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => run('/open')}>
                Open round (sweep applications + snapshot capacity)
              </button>
            ) : null}
            {round.status === 'OPEN' ? (
              <button className="btn-success !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => run('/run')}>
                Run placement engine
              </button>
            ) : null}
            {round.status === 'RUN' ? (
              <button className="btn-secondary !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => run('/close')}>
                Close round
              </button>
            ) : null}
            {round.status === 'CLOSED' ? <p className="text-xs text-slate-400">This round is closed.</p> : null}
          </div>
        </div>
      )}
    </Modal>
  );
}

function ApplicationsTab({ isOfficer }: { isOfficer: boolean }) {
  const [intake, setIntake] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (intake) qs.set('intake', intake);
  if (status) qs.set('status', status);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<AdmissionApplication>>(isOfficer ? `/api/v1/m28/admissions/applications?${qs.toString()}` : null);

  if (!isOfficer) return null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-52" placeholder="Search student NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="field-input !w-40" value={intake} onChange={(e) => setIntake(e.target.value)}>
          <option value="">All intakes</option>
          {ADMISSION_INTAKES.map((i) => (
            <option key={i} value={i}>
              {i}
            </option>
          ))}
        </select>
        <select className="field-input !w-40" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {(['SUBMITTED', 'OFFERED', 'WAITLISTED', 'ACCEPTED', 'DECLINED', 'WITHDRAWN'] as const).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Student</th>
                <th className="table-head">Intake</th>
                <th className="table-head">District</th>
                <th className="table-head">State</th>
                <th className="table-head">Offered school</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(a.id)}>
                  <td className="table-cell">
                    <div className="font-medium">{a.studentFullName}</div>
                    <div className="font-mono text-xs text-slate-400">{a.studentNin}</div>
                  </td>
                  <td className="table-cell">{a.intake}</td>
                  <td className="table-cell text-xs text-slate-500">{a.resolvedDistrict}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.state} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{a.offeredSchoolId ?? a.waitlistSchoolId ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId ? (
        <ApplicationDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function ApplicationDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<AdmissionApplication>(`/api/v1/m28/admissions/applications/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const app = detailQ.data;

  const withdraw = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m28/admissions/applications/${id}/withdraw`);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not withdraw application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Admission application" onClose={onClose} wide>
      {detailQ.loading || !app ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={app.state} />
            <span className="font-semibold">{app.studentFullName}</span>
            <span className="font-mono text-xs text-slate-400">{app.studentNin}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">DOB</dt>
              <dd className="font-medium">{formatDate(app.studentDob)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Guardian NIN</dt>
              <dd className="font-mono text-xs">{app.guardianNin}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Declared district</dt>
              <dd className="font-medium">{app.declaredDistrict}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Resolved district</dt>
              <dd className="font-medium">
                {app.resolvedDistrict} <span className="text-xs text-slate-400">({app.catchmentSource})</span>
              </dd>
            </div>
          </dl>
          <div className="border-t border-slate-100 pt-3">
            <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Preferences (rank order)</div>
            <ol className="list-decimal space-y-1 pl-5 text-xs">
              {[...app.preferences]
                .sort((a, b) => a.rank - b.rank)
                .map((p) => (
                  <li key={p.schoolId}>{p.schoolId}</li>
                ))}
            </ol>
          </div>
          {app.scoreBreakdown ? (
            <div className="border-t border-slate-100 pt-3">
              <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Score breakdown</div>
              <pre className="overflow-x-auto rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600">{JSON.stringify(app.scoreBreakdown, null, 2)}</pre>
            </div>
          ) : null}
          {app.state === 'OFFERED' ? <p className="text-xs text-slate-500">Offered: {app.offeredSchoolId} (preference #{app.offeredPreferenceRank})</p> : null}
          {app.state === 'WAITLISTED' ? (
            <p className="text-xs text-slate-500">
              Waitlisted for {app.waitlistSchoolId}
              {app.waitlistPosition ? ` — position ${app.waitlistPosition}` : ''}
            </p>
          ) : null}
          {!['ACCEPTED', 'DECLINED', 'WITHDRAWN'].includes(app.state) ? (
            <div className="border-t border-slate-100 pt-3">
              <button className="btn-danger !px-2.5 !py-1 text-xs" disabled={busy} onClick={withdraw}>
                Withdraw application
              </button>
              <p className="mt-1 text-[11px] text-slate-400">
                Accepting or declining an offer is a guardian self-service action and cannot be done from this console.
              </p>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
